import type { QueryRowType, Transaction } from "@rocicorp/zero";
import type { ConnectedAppUsageType } from "../db/schema";
import { type Schema, type Space, zql } from "./generatedDrizzleSchema";
import { lockCommitFacets } from "./commitFacetLock";
import { refreshConnectorUsages } from "./refreshConnectorUsages";
import type { Permission } from "./rbac/permissions";
import { buildUserGroupMemberships, type GrantRow } from "./rbac/grantQueries";
import {
  CROSS_RESOURCE_RULE_IDS,
  isSupplierAvailable,
  type CrossResourceAvailabilityRuleId,
  crossResourceRule,
  principalsWithPermission,
  principalHasSupplierPermissionFromGrants,
  requiredSupplierPermissions,
  type RbacReader,
  type ScopedGrantRow,
  userHasSupplierPermissionFromGrantReader,
  zqlGrantReader,
} from "./rbac/crossResourceAvailability";
import { grantHasExactPermission } from "./rbac/roles";
import { connectorUsagePolicies } from "./connectorUsagePolicies";
import { personalUseBlocked } from "./personalUse";
import { grantsForScope } from "./rbac/grantQueries";

type UsageRef<Type extends ConnectedAppUsageType = ConnectedAppUsageType> = {
  type: Type;
  refId: string;
  subKey: string;
};

type CamelCase<S extends string> = S extends `${infer Head}_${infer Tail}`
  ? `${Head}${Capitalize<CamelCase<Tail>>}`
  : S;

type UsageRefFactories = {
  [Type in ConnectedAppUsageType as CamelCase<Type>]: (
    ...args: string[]
  ) => UsageRef<Type>;
};

const approvalConnectorPermissions: readonly Permission[] = [
  "connected_app:read",
  "connected_app:use",
];

export const usageRef = {
  personalChat: (personalChatId: string) => ({
    type: "personal_chat",
    refId: personalChatId,
    subKey: "",
  }),
  workflowSlug: (workflowId: string, slug: string) => ({
    type: "workflow_slug",
    refId: workflowId,
    subKey: slug,
  }),
  commitStep: (commitId: string, stepDir: string) => ({
    type: "commit_step",
    refId: commitId,
    subKey: stepDir,
  }),
  workflowChat: (workflowId: string) => ({
    type: "workflow_chat",
    refId: workflowId,
    subKey: "",
  }),
  spaceGitRemote: (spaceId: string) => ({
    type: "space_git_remote",
    refId: spaceId,
    subKey: "",
  }),
  skillGitSource: (sourceId: string) => ({
    type: "skill_git_source",
    refId: sourceId,
    subKey: "",
  }),
  auditLogDestination: (destinationId: string) => ({
    type: "audit_log_destination",
    refId: destinationId,
    subKey: "",
  }),
  aiProvider: (aiProviderId: string) => ({
    type: "ai_provider",
    refId: aiProviderId,
    subKey: "",
  }),
} satisfies UsageRefFactories;

export async function recordConnectorUsage(
  tx: Transaction,
  connectedAppId: string,
  ref: UsageRef,
  tenantId: string
) {
  if (ref.type === "commit_step") await lockCommitFacets(tx, ref.refId);
  await tx.mutate.connectedAppUsages.upsert({
    connectedAppId,
    type: ref.type,
    refId: ref.refId,
    subKey: ref.subKey,
    tenantId,
    createdAt: Date.now(),
  });
  if (ref.type === "commit_step")
    await tx.mutate.commitConnectorUsages.upsert({
      commitId: ref.refId,
      connectorId: connectedAppId,
      tenantId,
    });
  if (ref.type === "commit_step") await refreshConnectorUsages(tx, ref.refId);
}

export async function dropConnectorUsage(
  tx: Transaction,
  connectedAppId: string,
  ref: UsageRef
) {
  if (ref.type === "commit_step") await lockCommitFacets(tx, ref.refId);
  await tx.mutate.connectedAppUsages.delete({
    connectedAppId,
    type: ref.type,
    refId: ref.refId,
    subKey: ref.subKey,
  });
  if (ref.type === "commit_step") {
    const remaining = await tx.run(
      zql.connectedAppUsages
        .where("type", "commit_step")
        .where("refId", ref.refId)
        .where("connectedAppId", connectedAppId)
        .one()
    );
    if (!remaining)
      await tx.mutate.commitConnectorUsages.delete({
        commitId: ref.refId,
        connectorId: connectedAppId,
      });
    await refreshConnectorUsages(tx, ref.refId);
  }
}

export function commitStepUsageQuery(connectedAppIds: readonly string[]) {
  return zql.connectedAppUsages
    .where("type", "commit_step")
    .where("connectedAppId", "IN", connectedAppIds)
    .whereExists("branchesAtHead")
    .related("commit", (c) =>
      c.related("branch", (b) => b.related("workflow").one()).one()
    );
}

export type CommitStepUsageRow = QueryRowType<
  ReturnType<typeof commitStepUsageQuery>
>;

export type BranchHeadWorkflowUsage = {
  connectedAppId: string;
  workflowId: string;
  spaceId: string;
  stepDir: string;
  branchId: string;
  branchName: string;
  // Head of the main branch — the live, deployed version of the workflow.
  live: boolean;
};

// Branches are a property of the space, not the viewer.
export function branchHeadWorkflowUsage(
  row: CommitStepUsageRow
): BranchHeadWorkflowUsage | null {
  const branch = row.commit?.branch;
  const workflow = branch?.workflow;
  if (!branch || branch.headCommitId !== row.refId) return null;
  if (!workflow || workflow.deletedAt != null) return null;
  return {
    connectedAppId: row.connectedAppId,
    workflowId: workflow.id,
    spaceId: workflow.spaceId,
    stepDir: row.subKey,
    branchId: branch.id,
    branchName: branch.name,
    live: branch.name === "main",
  };
}

function supplierPermissionsFor(
  ruleId: CrossResourceAvailabilityRuleId
): Permission[] {
  return [
    ...new Set(
      crossResourceRule(ruleId).tiers.flatMap(
        (tier) => tier.supplierPermissions
      )
    ),
  ];
}

async function dependentIdsUsingSupplier(
  grantReader: RbacReader,
  ruleId: CrossResourceAvailabilityRuleId,
  supplierId: string
): Promise<Set<string>> {
  const rule = crossResourceRule(ruleId);
  if (!rule.usageEdges) return new Set();
  const edges = await rule.usageEdges(grantReader, { supplierId });
  return new Set(edges.map((e) => e.dependentId));
}

// Usages a leaving user can no longer reach are dropped rather than blocking
// the removal; `excludeGroupId` simulates the removal, so this is correct
// whether the membership grants are deleted before or after it runs.
export async function dropUsagesLostByGroupRemoval(
  tx: Transaction,
  tenantId: string,
  groupId: string,
  removedUserIds: readonly string[]
): Promise<void> {
  if (removedUserIds.length === 0) return;

  const grantReader = zqlGrantReader(tx, tenantId);
  const groupGrants = await grantReader.grantsForPrincipal({
    userGroupId: groupId,
  });
  const lostApprovalAccess = new Map<string, Set<string>>();

  for (const ruleId of CROSS_RESOURCE_RULE_IDS) {
    const rule = crossResourceRule(ruleId);
    if (rule.supplier.scope !== "connected_app") continue;
    if (rule.skipRemovalEnforcement) continue;

    const suppliersProviding = (permission: Permission) =>
      groupGrants
        .filter((g) => grantHasExactPermission(g, permission))
        .map((g) => g.scopeId);

    for (const userId of removedUserIds) {
      const userGrants = await grantReader.grantsForPrincipal({ userId });
      const otherGroupIds = new Set(
        userGrants.map((g) => g.scopeId).filter((id) => id !== groupId)
      );

      for (const permission of supplierPermissionsFor(ruleId)) {
        for (const supplierId of suppliersProviding(permission)) {
          if (
            await userHasSupplierPermissionFromGrantReader(
              grantReader,
              userId,
              supplierId,
              permission,
              groupId
            )
          )
            continue;

          if (approvalConnectorPermissions.includes(permission)) {
            const users =
              lostApprovalAccess.get(supplierId) ?? new Set<string>();
            users.add(userId);
            lostApprovalAccess.set(supplierId, users);
          }

          if (rule.syntheticDependents)
            await dropOwnerConnectorUsages(tx, supplierId, userId);

          if (rule.usageEdges) {
            const dependentIds = await dependentIdsUsingSupplier(
              grantReader,
              ruleId,
              supplierId
            );
            if (dependentIds.size === 0) continue;
            const dependentGrants = await grantReader.grantsForScopes([
              ...dependentIds,
            ]);
            const reachedSpaceIds = new Set(
              dependentGrants
                .filter(
                  (g) =>
                    requiredSupplierPermissions(ruleId, g).includes(
                      permission
                    ) &&
                    (g.userId === userId ||
                      (g.userGroupId != null &&
                        otherGroupIds.has(g.userGroupId)))
                )
                .map((g) => g.scopeId)
            );
            for (const spaceId of reachedSpaceIds)
              await dropSpaceConnectorUsages(tx, supplierId, spaceId);
          }
        }
      }
    }
  }
  for (const [connectorId, userIds] of lostApprovalAccess)
    await dropConnectorApprovalsForLostAccess(
      tx,
      grantReader,
      connectorId,
      { userIds, groupIds: new Set(), serviceAccountIds: new Set() },
      groupId
    );
}

// Removing a principal's grants on a connector disconnects the dependents that
// relied on them (space workflows, git remotes, personal chats) rather than
// blocking the removal. Runs post-diff, so any route a principal keeps is
// respected.
export async function dropUsagesLostByGrantRemoval(
  tx: Transaction,
  tenantId: string,
  connectedAppId: string,
  removedGroupIds: readonly string[],
  removedUserIds: readonly string[],
  removedServiceAccountIds: readonly string[]
): Promise<void> {
  if (
    removedGroupIds.length === 0 &&
    removedUserIds.length === 0 &&
    removedServiceAccountIds.length === 0
  )
    return;

  const grantReader = zqlGrantReader(tx, tenantId);
  const remainingGrants = await grantReader.grantsForScope(connectedAppId);

  // Removed principals that actually lost `permission` — a principal that
  // keeps another grant with the same permission on the connector is unaffected.
  const principalsRemoved = (permission: Permission) => {
    const still = principalsWithPermission(remainingGrants, permission);
    return {
      groupIds: removedGroupIds.filter((id) => !still.groupIds.has(id)),
      userIds: removedUserIds.filter((id) => !still.userIds.has(id)),
      serviceAccountIds: removedServiceAccountIds.filter(
        (id) => !still.serviceAccountIds.has(id)
      ),
    };
  };

  // Users who lost `permission`: removed directly or via a removed group's
  // membership, and keeping it through no other grant post-diff.
  const lostUsersByPermission = new Map<Permission, Set<string>>();
  const lostUsers = async (
    permission: Permission,
    removed: { groupIds: string[]; userIds: string[] }
  ): Promise<Set<string>> => {
    const cached = lostUsersByPermission.get(permission);
    if (cached) return cached;
    const candidates = new Set(removed.userIds);
    if (removed.groupIds.length > 0) {
      const memberGrants = await grantReader.grantsForScopes(removed.groupIds);
      for (const g of memberGrants) if (g.userId) candidates.add(g.userId);
    }
    const lost = new Set<string>();
    for (const userId of candidates) {
      if (
        !(await userHasSupplierPermissionFromGrantReader(
          grantReader,
          userId,
          connectedAppId,
          permission
        ))
      )
        lost.add(userId);
    }
    lostUsersByPermission.set(permission, lost);
    return lost;
  };

  const lostApprovalAccess = {
    userIds: new Set<string>(),
    groupIds: new Set<string>(),
    serviceAccountIds: new Set<string>(),
  };
  for (const permission of approvalConnectorPermissions) {
    const removed = principalsRemoved(permission);
    for (const userId of await lostUsers(permission, removed))
      lostApprovalAccess.userIds.add(userId);
    for (const groupId of removed.groupIds)
      lostApprovalAccess.groupIds.add(groupId);
    for (const serviceAccountId of removed.serviceAccountIds)
      lostApprovalAccess.serviceAccountIds.add(serviceAccountId);
  }
  await dropConnectorApprovalsForLostAccess(
    tx,
    grantReader,
    connectedAppId,
    lostApprovalAccess
  );

  for (const ruleId of CROSS_RESOURCE_RULE_IDS) {
    const rule = crossResourceRule(ruleId);
    if (rule.supplier.scope !== "connected_app") continue;
    if (rule.skipRemovalEnforcement) continue;

    let dependentGrants: ScopedGrantRow[] = [];
    if (rule.usageEdges) {
      const dependentIds = await dependentIdsUsingSupplier(
        grantReader,
        ruleId,
        connectedAppId
      );
      if (dependentIds.size > 0)
        dependentGrants = await grantReader.grantsForScopes([...dependentIds]);
    }
    const syntheticDependents = rule.syntheticDependents
      ? await rule.syntheticDependents(grantReader, connectedAppId)
      : [];

    const brokenSpaceIds = new Set<string>();
    const brokenOwnerIds = new Set<string>();

    for (const permission of supplierPermissionsFor(ruleId)) {
      const removed = principalsRemoved(permission);
      if (
        removed.groupIds.length === 0 &&
        removed.userIds.length === 0 &&
        removed.serviceAccountIds.length === 0
      )
        continue;

      const removedGroups = new Set(removed.groupIds);
      const removedServiceAccounts = new Set(removed.serviceAccountIds);
      const lost = await lostUsers(permission, removed);

      for (const g of dependentGrants) {
        if (!requiredSupplierPermissions(ruleId, g).includes(permission))
          continue;
        // A group's or service account's grant must be covered by that
        // principal directly (a service account holds only its own grants,
        // never a group's); a user's grant accepts any of their remaining
        // routes.
        if (g.userGroupId != null && removedGroups.has(g.userGroupId))
          brokenSpaceIds.add(g.scopeId);
        else if (
          g.serviceAccountId != null &&
          removedServiceAccounts.has(g.serviceAccountId)
        )
          brokenSpaceIds.add(g.scopeId);
        else if (g.userId != null && lost.has(g.userId))
          brokenSpaceIds.add(g.scopeId);
      }

      for (const d of syntheticDependents) {
        if (d.permission === permission && lost.has(d.userId))
          brokenOwnerIds.add(d.userId);
      }
    }

    for (const spaceId of brokenSpaceIds)
      await dropSpaceConnectorUsages(tx, connectedAppId, spaceId);
    for (const userId of brokenOwnerIds)
      await dropOwnerConnectorUsages(tx, connectedAppId, userId);
  }
}

async function dropConnectorApprovalsForLostAccess(
  tx: Transaction<Schema>,
  grantReader: RbacReader,
  connectorId: string,
  lost: ReturnType<typeof principalsWithPermission>,
  excludedGroupId?: string
): Promise<void> {
  if (!lost.userIds.size && !lost.groupIds.size && !lost.serviceAccountIds.size)
    return;
  const approvals = (
    await Promise.all(
      connectorUsagePolicies.flatMap(({ accessLoss }) =>
        "approval" in accessLoss
          ? [accessLoss.approval(tx, { connectorId })]
          : []
      )
    )
  ).flat();
  if (approvals.length === 0) return;
  const spaceIds = new Set(
    approvals.flatMap((row) => (row.spaceId ? [row.spaceId] : []))
  );
  const spaceGrants = await grantReader.grantsForScopes([...spaceIds]);
  const groupIds = new Set(
    spaceGrants.flatMap((grant) =>
      grant.userGroupId && grant.userGroupId !== excludedGroupId
        ? [grant.userGroupId]
        : []
    )
  );
  const memberships =
    lost.userIds.size && groupIds.size
      ? await grantReader.membershipGrants([...lost.userIds], [...groupIds])
      : [];
  const reachedGroups = new Set(memberships.map((grant) => grant.scopeId));
  const affectedSpaces = new Set(
    spaceGrants
      .filter(
        (grant) =>
          (grant.userId && lost.userIds.has(grant.userId)) ||
          (grant.userGroupId &&
            (lost.groupIds.has(grant.userGroupId) ||
              reachedGroups.has(grant.userGroupId))) ||
          (grant.serviceAccountId &&
            lost.serviceAccountIds.has(grant.serviceAccountId))
      )
      .map((grant) => grant.scopeId)
  );
  for (const row of approvals)
    if (row.spaceId && affectedSpaces.has(row.spaceId))
      await dropConnectorUsage(tx, connectorId, row);
}

type ConnectorApprovalChange = {
  spaceId: string;
  principals: readonly Pick<
    GrantRow,
    "userId" | "userGroupId" | "serviceAccountId"
  >[];
};

export async function dropConnectorApprovalsWithoutAccess(
  tx: Transaction<Schema>,
  tenantId: string,
  changes: readonly ConnectorApprovalChange[]
): Promise<void> {
  const scopes = await Promise.all(
    changes
      .filter(({ principals }) => principals.length > 0)
      .map(async (change) => ({
        ...change,
        approvals: (
          await Promise.all(
            connectorUsagePolicies.flatMap(({ membership }) =>
              "dismiss" in membership
                ? [membership.dismiss(tx, { spaceId: change.spaceId })]
                : []
            )
          )
        ).flat(),
      }))
  );
  const activeScopes = scopes.filter(({ approvals }) => approvals.length > 0);
  if (activeScopes.length === 0) return;

  const userIds = [
    ...new Set(
      activeScopes.flatMap(({ principals }) =>
        principals.flatMap(({ userId }) => (userId ? [userId] : []))
      )
    ),
  ];
  const connectorIds = [
    ...new Set(
      activeScopes.flatMap(({ approvals }) =>
        approvals.map((row) => row.connectorId)
      )
    ),
  ];
  const grantReader = zqlGrantReader(tx, tenantId);
  const [memberships, connectorGrants] = await Promise.all([
    userIds.length
      ? tx.run(
          zql.roleGrants
            .where("tenantId", tenantId)
            .where("userId", "IN", userIds)
        )
      : [],
    grantReader.grantsForScopes(connectorIds),
  ]);
  const groupsByUser = buildUserGroupMemberships(memberships);
  const grantsByConnector = Map.groupBy(
    connectorGrants,
    (grant) => grant.scopeId
  );

  for (const { principals, approvals } of activeScopes) {
    for (const [connectorId, rows] of Map.groupBy(
      approvals,
      (row) => row.connectorId
    )) {
      const grants = grantsByConnector.get(connectorId) ?? [];
      const missingAccess = approvalConnectorPermissions.some((permission) => {
        const allowed = principalsWithPermission(grants, permission);
        return principals.some((principal) => {
          if (principal.userGroupId)
            return !allowed.groupIds.has(principal.userGroupId);
          if (principal.serviceAccountId)
            return !allowed.serviceAccountIds.has(principal.serviceAccountId);
          return (
            principal.userId != null &&
            !principalHasSupplierPermissionFromGrants(
              principal.userId,
              groupsByUser.get(principal.userId) ?? [],
              grants,
              permission
            )
          );
        });
      });
      if (!missingAccess) continue;
      for (const row of rows) await dropConnectorUsage(tx, connectorId, row);
    }
  }
}

async function dropOwnerConnectorUsages(
  tx: Transaction,
  connectedAppId: string,
  userId: string
): Promise<void> {
  for (const { accessLoss } of connectorUsagePolicies) {
    if (!("owner" in accessLoss)) continue;
    const rows = await accessLoss.owner(tx, {
      connectorId: connectedAppId,
      userId,
    });
    for (const row of rows) await dropConnectorUsage(tx, connectedAppId, row);
  }
}

export async function reconcileWorkflowConnectorUsagesOnMove(
  tx: Transaction<Schema>,
  workflowId: string,
  targetSpace: Pick<Space, "id" | "tenantId" | "mode">
): Promise<void> {
  const connectorIds = new Set<string>();
  for (const { spaceMove } of connectorUsagePolicies) {
    if ("dismiss" in spaceMove) {
      for (const row of await spaceMove.dismiss(tx, { workflowId }))
        await dropConnectorUsage(tx, row.connectorId, row);
    } else if ("check" in spaceMove) {
      for (const connectorId of await spaceMove.check(tx, workflowId))
        connectorIds.add(connectorId);
    }
  }
  if (!connectorIds.size) return;
  const targetGrants = await tx.run(
    grantsForScope(targetSpace.id, targetSpace.tenantId)
  );
  for (const connectorId of connectorIds) {
    const [connector] = await tx.run(
      zql.connectedApps.where("id", connectorId)
    );
    // A grantless destination must not inherit connector associations through vacuous availability.
    const available =
      connector != null &&
      targetGrants.length > 0 &&
      !personalUseBlocked(connector, {
        kind: "space",
        mode: targetSpace.mode,
      }) &&
      (await isSupplierAvailable({
        tx,
        tenantId: targetSpace.tenantId,
        ruleId: "space_uses_connected_apps",
        dependentScopeId: targetSpace.id,
        supplierScopeId: connectorId,
      }));
    if (available) continue;
    await dropWorkflowConnectorUsages(tx, connectorId, workflowId);
  }
}

export async function dropWorkflowConnectorUsages(
  tx: Transaction,
  connectorId: string,
  workflowId: string
): Promise<void> {
  for (const { spaceMove } of connectorUsagePolicies) {
    if (!("check" in spaceMove)) continue;
    for (const row of await spaceMove.usages(tx, { workflowId, connectorId }))
      await dropConnectorUsage(tx, connectorId, row);
  }
}

async function dropSpaceConnectorUsages(
  tx: Transaction,
  connectedAppId: string,
  spaceId: string
): Promise<void> {
  for (const { accessLoss } of connectorUsagePolicies) {
    const lookup =
      "space" in accessLoss
        ? accessLoss.space
        : "approval" in accessLoss
          ? accessLoss.approval
          : null;
    if (!lookup) continue;
    for (const row of await lookup(tx, {
      connectorId: connectedAppId,
      spaceId,
    }))
      await dropConnectorUsage(tx, connectedAppId, row);
  }
}

export async function dropPersonalConnectorUsages(
  tx: Transaction,
  connectedAppId: string
): Promise<void> {
  for (const { personalUse } of connectorUsagePolicies) {
    const lookup =
      "space" in personalUse
        ? personalUse.space
        : "owner" in personalUse
          ? personalUse.owner
          : null;
    if (!lookup) continue;
    const usages = await lookup(tx, { connectorId: connectedAppId });
    const spaceIds = [
      ...new Set(usages.flatMap((row) => (row.spaceId ? [row.spaceId] : []))),
    ];
    const personalSpaces = new Set(
      spaceIds.length
        ? (
            await tx.run(
              zql.spaces.where("id", "IN", spaceIds).where("mode", "personal")
            )
          ).map((space) => space.id)
        : []
    );
    for (const row of usages)
      if (
        "owner" in personalUse ||
        (row.spaceId && personalSpaces.has(row.spaceId))
      )
        await dropConnectorUsage(tx, connectedAppId, row);
  }
}
